import { BLOCKS } from '@contentful/rich-text-types';
import classNames from 'classnames';
import { map, get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container, Grid, Cell } from 'components/Grid';
import { TertiaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import Paragraph from 'components/Paragraph';
import RichText from 'components/RichText';
import Slide from 'models/PageSectionTestimonialCarousel/Slide';
import Theme from 'styles/theme';

import { DESKTOP_CONTENT_PADDING } from './constants';
import Dots from './Dots';

class TestimonialCarouselDesktop extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      autoplay: true,
    };

    this.startAutoplay();
  }

  componentWillUnmount() {
    this.disableAutoplay();
  }

  startAutoplay = () => {
    const { interval } = this.props;
    this.intervalId = setInterval(this.showNext, interval);
  };

  disableAutoplay = () => {
    clearInterval(this.intervalId);
    this.setState({ autoplay: false });
  };

  showNext = () => {
    const { slides, activeSlideIndex, handleChange } = this.props;

    const nSlides = get(slides, 'length');
    const nextSlideIndex = activeSlideIndex < nSlides - 1 ? activeSlideIndex + 1 : 0;

    handleChange(nextSlideIndex);
  };

  handleSlideSelect = index => {
    const { activeSlideIndex, handleChange } = this.props;
    const { autoplay } = this.state;
    if (index === activeSlideIndex) return;

    // disable autoplay once user interacts with carousel controls
    if (autoplay) {
      this.disableAutoplay();
    }

    handleChange(index);
  };

  render() {
    const { slides, activeSlideIndex } = this.props;

    const options = {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => (
          <p className="TestimonialCarouselDesktop__subtext">{children}</p>
        ),
      },
    };

    return (
      <Container>
        <div className="TestimonialCarouselDesktop">
          {map(slides, (slide, index) => (
            <div
              key={index}
              className={classNames('TestimonialCarouselDesktop__slide', {
                'TestimonialCarouselDesktop__slide--active': index === activeSlideIndex,
              })}
            >
              <Grid>
                <Cell largeUp={{ columns: 5 }}>
                  <div className="TestimonialCarouselDesktop__image-wrapper">
                    <Image
                      className="TestimonialCarouselDesktop__image"
                      src={get(slide, ['imageLandscape', 'file', 'url'])}
                      alt={get(slide, ['imageLandscape', 'description'])}
                      width={get(slide, ['imageLandscape', 'file', 'details', 'image', 'width'])}
                      height={get(slide, ['imageLandscape', 'file', 'details', 'image', 'height'])}
                    />
                  </div>
                </Cell>
                <Cell largeUp={{ columns: 7 }}>
                  <div className="TestimonialCarouselDesktop__content-wrapper">
                    <TertiaryHeading className="TestimonialCarouselDesktop__header">
                      {get(slide, 'heading')}
                    </TertiaryHeading>
                    <RichText content={get(slide, 'subtext')} options={options} />
                    {!isEmpty(get(slide, 'description')) && (
                      <Paragraph className="TestimonialCarouselDesktop__description">
                        {get(slide, 'description')}
                      </Paragraph>
                    )}
                  </div>
                </Cell>
              </Grid>
            </div>
          ))}
          <Grid>
            <Cell largeUp={{ columns: 5 }}>&nbsp;{/* layout placeholder */}</Cell>
            <Cell largeUp={{ columns: 7 }}>
              {get(slides, 'length') > 1 && (
                <Dots
                  nDots={get(slides, 'length')}
                  activeIndex={activeSlideIndex}
                  onClick={this.handleSlideSelect}
                />
              )}
            </Cell>
          </Grid>

          <style jsx>
            {`
              .TestimonialCarouselDesktop {
                position: relative;
              }

              .TestimonialCarouselDesktop__slide {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                opacity: 0;
                transition: opacity 1s ease;
                pointer-events: none;
                padding: 0 ${Theme.get('spacing') * 8}px;
              }

              .TestimonialCarouselDesktop__slide:first-child {
                position: relative;
              }

              .TestimonialCarouselDesktop__slide--active {
                opacity: 1;
                pointer-events: auto;
              }

              .TestimonialCarouselDesktop__content-wrapper {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                padding-left: ${DESKTOP_CONTENT_PADDING}px;
                padding-right: ${Theme.get('spacing') * 5}px;
              }
            `}
          </style>
          <style jsx global>
            {`
              .TestimonialCarouselDesktop__header.typography--tertiary-heading {
                padding-bottom: ${Theme.get('spacing') * 7}px;
              }

              .RichText .TestimonialCarouselDesktop__subtext {
                font-weight: ${Theme.get('font-weight-bold')};
                font-size: ${Theme.get('font-size-small')}px;
                line-height: ${Theme.get('line-height')};
                letter-spacing: 1.7px;
                text-transform: uppercase;
              }

              .TestimonialCarouselDesktop__image.ImageV2 {
                height: 100%;
              }

              .TestimonialCarouselDesktop__description.Paragraph {
                margin-bottom: 0;
                margin-top: ${Theme.get('spacing')}px;
                font-size: ${Theme.get('font-size-description-px')};
              }
            `}
          </style>
        </div>
      </Container>
    );
  }
}

TestimonialCarouselDesktop.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.instanceOf(Slide)).isRequired,
  interval: PropTypes.number,
  activeSlideIndex: PropTypes.number,
  handleChange: PropTypes.func,
};

TestimonialCarouselDesktop.defaultProps = {
  interval: 4000,
  activeSlideIndex: 0,
  handleChange: () => {},
};

export default TestimonialCarouselDesktop;
